import { ref, deleteObject } from 'firebase/storage';
import { storage } from '../firebaseConfig';

/**
 * Extract the storage path from a Firebase Storage URL
 * @param {string} imageUrl - Full Firebase Storage URL
 * @returns {string|null} - Storage path or null if invalid
 */
export const extractStoragePath = (imageUrl) => {
    if (!imageUrl) return null;

    try {
        // Firebase Storage URLs contain the path after /o/
        // Example: https://firebasestorage.googleapis.com/v0/b/bucket/o/images%2Ffilename.jpg?token=...
        const urlParts = imageUrl.split('/o/');
        if (urlParts.length < 2) return null;

        // Get the path part and remove query parameters
        const pathWithQuery = urlParts[1].split('?')[0];

        // Decode the URL-encoded path
        const decodedPath = decodeURIComponent(pathWithQuery);

        return decodedPath;
    } catch (error) {
        console.error('Error extracting storage path:', error);
        return null;
    }
};

/**
 * Delete an image from Firebase Storage
 * @param {string} imageUrl - Full Firebase Storage URL
 * @returns {Promise<boolean>} - True if deleted successfully, false otherwise
 */
export const deleteImageFromStorage = async (imageUrl) => {
    if (!imageUrl) {
        return true;
    }

    try {
        const storagePath = extractStoragePath(imageUrl);

        if (!storagePath) {
            console.error('Could not extract storage path from URL');
            return false;
        }

        const imageRef = ref(storage, storagePath);
        await deleteObject(imageRef);

        return true;
    } catch (error) {
        // If file doesn't exist (404), consider it a success
        if (error.code === 'storage/object-not-found') {
            return true;
        }

        console.error('Error deleting image from storage:', error.code);
        return false;
    }
};

/**
 * Delete multiple images from Firebase Storage
 * @param {Array<string>} imageUrls - Array of Firebase Storage URLs
 * @returns {Promise<{success: number, failed: number}>} - Count of successful and failed deletions
 */
export const deleteMultipleImages = async (imageUrls) => {
    let success = 0;
    let failed = 0;

    for (const url of imageUrls) {
        const result = await deleteImageFromStorage(url);
        if (result) {
            success++;
        } else {
            failed++;
        }
    }

    return { success, failed };
};
